import { FC, PropsWithChildren, useState, useEffect, useRef } from 'react';
import InViewport from '../InViewport/InViewport.tsx';
import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { ContentImpressionEvent } from '@moment/analytics/AnalyticsService.ts';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useContext } from 'react';
import { getSSRTranslation } from '../../../i18n.ts';
import { EnhancedLightboxItem } from '@moment/net/softbox-types.ts';
import { IInterestResponse } from '@pages/Root/services/api/interest/interest-model-definitions.ts';
import { getCurrentNumberOfInteractions } from '@moment/local-personalization/scorer.ts';

const CONTENT_MIN_VIEW_TIME = 1000; // 1 second
const CONTENT_MIN_VIEW_PART = 0.5; // 50% of component must be visible

interface ContentImpressionTrackerProps extends PropsWithChildren {
  item: EnhancedLightboxItem;
  interests: IInterestResponse;
  source?: string;
  schedule?: string;
}

const ContentImpressionTracker: FC<ContentImpressionTrackerProps> = ({
  children,
  item,
  interests,
  source,
  schedule,
}) => {
  const [, setViewTime] = useState<{ date: number; isEmitted: boolean } | null>(null);
  const analytics = useAnalytics();
  const context = useContext(CommonPageDataContext);
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasEmittedRef = useRef<boolean>(false);

  const onItemInView = () => {
    // Don't set up tracking if we've already emitted
    if (hasEmittedRef.current) {
      return;
    }

    setViewTime((prevState) => {
      // Preserve isEmitted state if already set
      return {
        date: new Date().getTime(),
        isEmitted: prevState?.isEmitted || false,
      };
    });

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set timeout to check if item has been viewed for minimum time
    timeoutRef.current = setTimeout(() => {
      setViewTime((prevState) => {
        const currentItem = prevState;

        if (currentItem?.date && !currentItem.isEmitted && !hasEmittedRef.current) {
          const isShouldEmit =
            new Date().getTime() - currentItem.date >= CONTENT_MIN_VIEW_TIME;

          if (isShouldEmit) {
            const primaryInterest = item.interests && item.interests.length > 0
              ? item.interests[0]
              : '';

            const interest = interests.interests.find((i) => i.uid === primaryInterest);
            const interestName = interest
              ? getSSRTranslation(interest.name, i18n)
              : '';

            // note - this is done every few cycles, but, we need to make sure we can still fling without issues
            const event: ContentImpressionEvent = {
              numInteractions: getCurrentNumberOfInteractions(),
              partnerId: context.partner || '',
              contentId: item.uid,
              title: getSSRTranslation(item.title, i18n),
              contentOwner: item.owner,
              primaryInterest: interestName,
              language: i18n.language,
              source: source || window.location.href,
              searchParams,
              schedule,
            };

            analytics.emitContentImpressionEvent(event);
            hasEmittedRef.current = true;
          }

          return {
            ...currentItem,
            isEmitted: isShouldEmit,
          };
        }

        return prevState;
      });
    }, CONTENT_MIN_VIEW_TIME);
  };

  const onHidden = () => {
    setViewTime((prevState) => {
      if (prevState) {
        // Clear timeout when item is hidden
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return {
          ...prevState,
          date: 0,
        };
      }
      return prevState;
    });
  };

  useEffect(() => {
    return () => {
      // Cleanup timeout on unmount
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <InViewport
      threshold={CONTENT_MIN_VIEW_PART}
      onView={onItemInView}
      onHidden={onHidden}
    >
      {children}
    </InViewport>
  );
};

export default ContentImpressionTracker;

